/* ============================================================
   slideshow.js — <nmp-slideshow>, <nmp-rail> and <nmp-film>

   Three elements, one concern: media that moves. They share no code path but
   they share a rule — none is required for its section to work. The hero
   renders its first slide from Liquid, the rail scrolls natively and a film
   shows its poster, so a visitor with no JavaScript, a slow connection or a
   reduced-motion setting gets a complete, styled, usable section. Everything
   here is enhancement.

   Neither element contains any styling. Both write their state to data
   attributes and let the section's own stylesheet decide what that looks
   like, which is what keeps colours and motion in the token layer.

   <nmp-slideshow> markup contract (see sections/hero-slideshow.liquid):

     <nmp-slideshow data-autoplay data-interval="5" data-label-video="video">
       <div data-slide data-active> … <video> … </div>
       <div data-slide aria-hidden="true"> … </div>
       <button data-dot aria-current="true"></button>
       <span data-counter>01 / 02</span>
     </nmp-slideshow>

   <nmp-film> markup contract (see sections/film-block.liquid, M-08):

     <nmp-film data-autoplay>          omit data-autoplay for tap-to-play only
       … snippets/media.liquid video slot …
       <button data-film-play>play</button>
     </nmp-film>

   Video policy lives here rather than in the markup: the media snippet never
   writes an autoplay attribute, so a video plays only when this file says it
   may — for a slide, when the slide is active; for a film, when the section
   asked for autoplay and the viewport is at least 1024px — and in both cases
   only when the element is on screen and the visitor has asked for neither
   reduced motion nor data saving. Everything else falls back to the poster,
   which is why docs/06 makes the poster mandatory.

   Last updated: 2026-09-07
   ============================================================ */
(function () {
  'use strict';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Data saver is not implemented in every browser — Safari has no
     navigator.connection at all — so the whole chain is guarded. */
  function savesData() {
    return !!(navigator.connection && navigator.connection.saveData);
  }

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  class NmpSlideshow extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;

      /* Guard before _ready, same rule as NmpRail and NmpFilm below. */
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
      if (this.slides.length === 0) return;

      this._ready = true;

      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-dot]'));
      this.counter = this.querySelector('[data-counter]');
      this.videoLabel = this.getAttribute('data-label-video') || '';

      var interval = parseInt(this.getAttribute('data-interval'), 10);
      this.interval = (isNaN(interval) ? 5 : Math.min(Math.max(interval, 3), 10)) * 1000;

      /* Autoplay is a request, not a promise: reduced motion and a single
         slide both veto it outright. */
      this.mayAutoplay =
        this.hasAttribute('data-autoplay') && this.slides.length > 1 && !prefersReducedMotion();

      this.index = Math.max(this.slides.indexOf(this.querySelector('[data-slide][data-active]')), 0);

      var self = this;
      this.dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          /* Once a visitor picks a slide, the carousel stops moving on its
             own. Fighting someone for control of the page is hostile. */
          self.mayAutoplay = false;
          self._stop();
          self.goTo(i);
        });
      });

      /* WCAG 2.2.2's mechanism. Choosing a dot has always stopped the
         carousel, but a dot is not announced as a stop control and the dots
         are a setting the owner can switch off. This button is rendered only
         when autoplay is on, and it is removed outright under reduced motion,
         where nothing is moving and a control over nothing is worse than no
         control. Stopping is one-way, like the dots: nobody asks for a hero
         to start moving again. */
      this.pauseButton = this.querySelector('[data-slideshow-pause]');
      if (this.pauseButton) {
        if (!this.mayAutoplay) {
          this.pauseButton.remove();
          this.pauseButton = null;
        } else {
          this.pauseButton.addEventListener('click', function () {
            self.mayAutoplay = false;
            self._stop();
            self.pauseButton.setAttribute('aria-disabled', 'true');
            self.pauseButton.textContent = self.getAttribute('data-label-paused') || '';
          });
        }
      }

      this._onVisibility = function () {
        if (document.hidden) self._stop();
        else if (self._onScreen) self._start();
      };
      document.addEventListener('visibilitychange', this._onVisibility);

      /* Re-evaluated live (2026-09-07): sampled once at connect, a visitor
         who switched reduced motion on mid-session kept the moving hero
         until reload. One-way, like the dots and the pause button. */
      this._rm = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._onRm = function () {
        if (!self._rm.matches) return;
        self.mayAutoplay = false;
        self._stop();
        if (self.pauseButton) {
          self.pauseButton.remove();
          self.pauseButton = null;
        }
      };
      this._rm.addEventListener('change', this._onRm);

      /* A hero that keeps advancing after it has scrolled out of view burns
         battery to animate something nobody is looking at. */
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          function (entries) {
            self._onScreen = entries[0].isIntersecting;
            if (self._onScreen) self._start();
            else self._stop();
          },
          { threshold: 0.25 }
        );
        this._observer.observe(this);
      } else {
        this._onScreen = true;
        this._start();
      }

      this.setAttribute('data-ready', '');
      this._render();
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this._stop();
      document.removeEventListener('visibilitychange', this._onVisibility);
      if (this._rm) this._rm.removeEventListener('change', this._onRm);
      if (this._observer) this._observer.disconnect();
    }

    goTo(next) {
      var total = this.slides.length;
      this.index = ((next % total) + total) % total;
      this._render();
      this.dispatchEvent(
        new CustomEvent('nmp:slide:change', { bubbles: true, detail: { index: this.index } })
      );
    }

    _start() {
      if (!this.mayAutoplay || this.timer) return;
      var self = this;
      this.timer = window.setInterval(function () {
        self.goTo(self.index + 1);
      }, this.interval);
      this.setAttribute('data-playing', 'true');
    }

    _stop() {
      window.clearInterval(this.timer);
      this.timer = null;
      this.setAttribute('data-playing', 'false');
    }

    _render() {
      var self = this;

      this.slides.forEach(function (slide, i) {
        var active = i === self.index;
        if (active) {
          slide.setAttribute('data-active', '');
          slide.removeAttribute('aria-hidden');
        } else {
          slide.removeAttribute('data-active');
          slide.setAttribute('aria-hidden', 'true');
        }
        self._video(slide, active);
      });

      this.dots.forEach(function (dot, i) {
        if (i === self.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (this.counter) {
        var text = pad(this.index + 1) + ' / ' + pad(this.slides.length);
        if (this.videoLabel && this.slides[this.index].querySelector('video')) {
          text += ' · ' + this.videoLabel;
        }
        this.counter.textContent = text;
      }
    }

    _video(slide, active) {
      var video = slide.querySelector('video');
      if (!video) return;

      if (!active) {
        video.pause();
        slide.removeAttribute('data-video-playing');
        return;
      }

      /* The poster is a real image underneath, so refusing to play here is a
         complete experience rather than a blank box. */
      if (prefersReducedMotion() || savesData()) return;

      var playing = video.play();
      if (playing && typeof playing.then === 'function') {
        playing
          .then(function () {
            slide.setAttribute('data-video-playing', '');
          })
          .catch(function () {
            /* Autoplay refused by the browser: the poster stays, which is
               exactly what we want to happen. */
          });
      } else {
        slide.setAttribute('data-video-playing', '');
      }
    }
  }

  class NmpRail extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;

      /* The guard comes BEFORE _ready, not after. A rail with no track — an
         empty collection, or a section caught mid-edit in the theme editor —
         used to mark itself ready and then bail, so the next disconnect ran
         `this.track.removeEventListener` on null and threw. NmpGallery in
         product.js has always had this order; this one did not. */
      this.track = this.querySelector('[data-rail-track]');
      if (!this.track) return;

      this._ready = true;

      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');

      var self = this;
      if (this.prev) this.prev.addEventListener('click', function () { self._scroll(-1); });
      if (this.next) this.next.addEventListener('click', function () { self._scroll(1); });

      this._update = this._update.bind(this);
      this._queue = function () {
        if (self._frame) return;
        self._frame = window.requestAnimationFrame(function () {
          self._frame = null;
          self._update();
        });
      };

      this.track.addEventListener('scroll', this._queue, { passive: true });
      window.addEventListener('resize', this._queue);

      if ('ResizeObserver' in window) {
        this._observer = new ResizeObserver(this._queue);
        this._observer.observe(this.track);
      }

      this.setAttribute('data-ready', '');
      this._update();
    }

    /* Bail if connectedCallback never got past its guard: a rail with no track
       left this.track null and the next disconnect threw. Does NOT reset
       _ready — re-init would double-bind the anonymous arrow handlers. */
    disconnectedCallback() {
      if (!this._ready) return;
      this.track.removeEventListener('scroll', this._queue);
      window.removeEventListener('resize', this._queue);
      if (this._observer) this._observer.disconnect();
      if (this._frame) window.cancelAnimationFrame(this._frame);
    }

    _scroll(direction) {
      this.track.scrollBy({
        left: direction * this.track.clientWidth * 0.8,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    _update() {
      var track = this.track;
      /* One pixel of slack: sub-pixel layout means scrollLeft rarely lands
         exactly on the end, and an arrow that never disables looks broken. */
      var overflows = track.scrollWidth - track.clientWidth > 1;
      var atStart = track.scrollLeft <= 1;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;

      this.setAttribute('data-overflow', overflows ? 'true' : 'false');
      if (atStart) this.setAttribute('data-at-start', '');
      else this.removeAttribute('data-at-start');
      if (atEnd) this.setAttribute('data-at-end', '');
      else this.removeAttribute('data-at-end');

      if (this.prev) this.prev.disabled = !overflows || atStart;
      if (this.next) this.next.disabled = !overflows || atEnd;
    }
  }

  class NmpFilm extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;

      /* Same ordering rule as NmpRail above. This one never threw only because
         its teardown touches nothing a bailed-out element lacks — luck, so it
         carries the same `_ready` guard. */
      this.video = this.querySelector('video');
      if (!this.video) return;

      this._ready = true;

      this.button = this.querySelector('[data-film-play]');
      var self = this;

      /* The button is a real control at every width, not a desktop
         decoration: autoplay is refused by every mobile browser worth
         naming, and a poster with no way to start it is a dead frame. On
         desktop it doubles as pause. */
      if (this.button) {
        this.button.addEventListener('click', function () {
          if (self.video.paused) {
            self._play(true);
          } else {
            self._userPaused = true;
            self._pause();
          }
        });
      }

      /* mayAutoplay is a request the browser can still refuse, and refusal
         is fine — the poster underneath is a complete experience. Same
         policy as a hero video slide, for the same reason. */
      this.mayAutoplay =
        this.hasAttribute('data-autoplay') &&
        window.matchMedia('(min-width: 1024px)').matches &&
        !prefersReducedMotion() &&
        !savesData();

      this.video.addEventListener('play', function () {
        self.setAttribute('data-video-playing', '');
        self.setAttribute('data-state', 'playing');
      });
      this.video.addEventListener('pause', function () {
        self.setAttribute('data-state', 'paused');
      });

      /* Playing a film nobody is looking at costs battery and bandwidth for
         nothing. Scrolling back to it resumes — unless the visitor pressed
         pause, which _userPaused remembers. */
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          function (entries) {
            if (entries[0].isIntersecting) self._play(false);
            else self._pause();
          },
          { threshold: 0.35 }
        );
        this._observer.observe(this);
      } else if (this.mayAutoplay) {
        this._play(false);
      }

      this.setAttribute('data-ready', '');
    }

    disconnectedCallback() {
      if (!this._ready) return;
      if (this._observer) this._observer.disconnect();
    }

    /* manual = the visitor pressed the button. Anything else is the observer
       scrolling the film back into view, which must not override a pause. */
    _play(manual) {
      if (manual) {
        this._userPaused = false;
        this._userPlayed = true;
      } else if (this._userPaused) {
        return;
      } else if (!this.mayAutoplay && !this._userPlayed) {
        return;
      }

      /* preload="none" on the element keeps a film off the wire until this
         moment, which is the whole reason a page can carry one cheaply. */
      if (this.video.preload === 'none') this.video.preload = 'auto';

      var playing = this.video.play();
      if (playing && typeof playing.catch === 'function') {
        playing.catch(function () {
          /* Refused. The poster stays, and the button is still there. */
        });
      }
    }

    _pause() {
      this.video.pause();
      this.removeAttribute('data-video-playing');
    }
  }

  if (!window.customElements.get('nmp-slideshow')) {
    window.customElements.define('nmp-slideshow', NmpSlideshow);
  }
  if (!window.customElements.get('nmp-rail')) {
    window.customElements.define('nmp-rail', NmpRail);
  }
  if (!window.customElements.get('nmp-film')) {
    window.customElements.define('nmp-film', NmpFilm);
  }

  /* ------------------------------------------- theme editor block select */

  /* Four sections emit this script and three of them may share a template;
     each <script> element executes the file again. The defines above are
     guarded per name — these document listeners were not, so two decks on
     one page ran every selection handler twice (2026-09-06). Same latch as
     capture.js and quick-add.js. */
  if (window.__nmpSlideshowEditor) return;
  window.__nmpSlideshowEditor = true;

  /* shopify.dev requires a selected block to "become, and remain, visible
     while it's selected", and gives a slideshow sliding to the selected slide
     as the worked example. Without it the owner opens slide 4 and edits it
     while looking at slide 1.

     The event target IS the slide: hero-slideshow.liquid:138-141 puts
     shopify_attributes on the same element as [data-slide], so nothing here
     needs a block id. _stop() rather than clearing mayAutoplay, because a
     selection is not the visitor asking the deck to stay stopped. */
  document.addEventListener('shopify:block:select', function (event) {
    var deck = event.target.closest && event.target.closest('nmp-slideshow');
    if (!deck || typeof deck.goTo !== 'function' || !deck.slides) return;
    var index = deck.slides.indexOf(event.target);
    if (index < 0) return;
    deck._stop();
    deck.goTo(index);
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var deck = event.target.closest && event.target.closest('nmp-slideshow');
    if (!deck || typeof deck._start !== 'function') return;
    /* _start already no-ops when the deck is off screen or the tab is hidden,
       so mayAutoplay is the only condition worth re-checking here. */
    if (deck.mayAutoplay) deck._start();
  });
})();
